import { Dispatch, Ref, SetStateAction, forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Divider, Stack } from '@mui/material';

import { useMutation } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  AdvertisingPackageResponse,
  BookingRequest,
  MediaPlanResponse,
  PackageBookingRequest,
  SpaceResponse,
} from '@/api/data-contracts';
import { ReactComponent as BackIcon } from '@/assets/icons/back-icon.svg';
import { ReactComponent as ShoppingCartIcon } from '@/assets/icons/cart-icon.svg';
import { ROUTES } from '@/config/routes';
import { useMediaPlansService } from '@/features/media-plans';
import { normalizeAvailabilityRange } from '@/shared/lib/availability';
import AppButton from '@/shared/ui/AppButton';
import useGeneratikTableStore from '@/shared/ui/GeneratikTable/generatikTableStore';

import ShoppingCartMediaPlanPickerPlanInput from './ShoppingCartMediaPlanPickerPlanInput';

type CartAvailability = { start: string; end: string };

interface Props {
  handleClose: () => void;
  setActiveStep: Dispatch<SetStateAction<number>>;
}

const ShoppingCartMediaPlanPicker = forwardRef(function ShoppingCartMediaPlanPicker(
  { handleClose, setActiveStep }: Props,
  ref: Ref<HTMLDivElement>,
) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addBookingsToMediaPlan, addPackageBookingsToMediaPlan } = useMediaPlansService();

  const [selectedMediaPlan, setSelectedMediaPlan] = useState<MediaPlanResponse | null>(null);

  const {
    selectedSpaces,
    selectedPackages,
    spacesStartDate,
    spacesEndDate,
    packagesStartDate,
    packagesEndDate,
    setSelectedRows,
  } = useGeneratikTableStore(
    useShallow((state) => {
      const spacesTable = state.tables['ADVERTISING_SPACES'];
      const packagesTable = state.tables['ADVERTISING_PACKAGES'];

      const spacesDateRange = spacesTable?.filterValues?.dateRange as
        | { start?: string; end?: string }
        | undefined;
      const packagesDateRange = packagesTable?.filterValues?.dateRange as
        | { start?: string; end?: string }
        | undefined;

      return {
        selectedSpaces: (state.selectedRows['ADVERTISING_SPACES'] ?? []) as SpaceResponse[],
        selectedPackages: (state.selectedRows['ADVERTISING_PACKAGES'] ??
          []) as AdvertisingPackageResponse[],
        setSelectedRows: state.setSelectedRows,
        spacesStartDate: spacesDateRange?.start,
        spacesEndDate: spacesDateRange?.end,
        packagesStartDate: packagesDateRange?.start,
        packagesEndDate: packagesDateRange?.end,
      };
    }),
  );

  const { mutateAsync: addSpacesToMediaPlan } = useMutation(() => {
    return addBookingsToMediaPlan(
      selectedMediaPlan!.id,
      selectedSpaces?.map((selectedSpace) => {
        const space = selectedSpace as { id: string; cartAvailability?: CartAvailability };

        return {
          availability: normalizeAvailabilityRange(
            space.cartAvailability?.start ?? spacesStartDate ?? '',
            space.cartAvailability?.end ?? spacesEndDate ?? '',
          ),
          space: { spaceId: space.id },
        };
      }) as BookingRequest[],
    );
  });

  const { mutateAsync: addPackagesToMediaPlan } = useMutation(() => {
    return addPackageBookingsToMediaPlan(
      selectedMediaPlan!.id,
      selectedPackages?.map((selectedPackage) => {
        const advertisingPackage = selectedPackage as {
          id: string;
          cartAvailability?: CartAvailability;
        };

        return {
          availability: normalizeAvailabilityRange(
            advertisingPackage.cartAvailability?.start ?? packagesStartDate ?? '',
            advertisingPackage.cartAvailability?.end ?? packagesEndDate ?? '',
          ),
          advertisingPackage: { advertisingPackageId: advertisingPackage.id },
        };
      }) as PackageBookingRequest[],
    );
  });

  const addToMediaPlan = async () => {
    await addSpacesToMediaPlan();
    await addPackagesToMediaPlan();

    handleClose();

    setSelectedRows('ADVERTISING_SPACES', []);
    setSelectedRows('ADVERTISING_PACKAGES', []);

    navigate(`${ROUTES.AUTHORIZED.MEDIA_PLANS}/${selectedMediaPlan!.id}`);
  };

  return (
    <Stack
      ref={ref}
      sx={{
        padding: '0.75rem 1rem',
        gap: '0.75rem',
      }}
    >
      <Stack alignItems="flex-start">
        <AppButton
          variant="ghost"
          size="small"
          startIcon={<BackIcon />}
          onClick={() => setActiveStep(0)}
        >
          {t('Back')}
        </AppButton>

        <Divider flexItem sx={{ margin: '0.375rem 0 !important' }} />
      </Stack>

      <ShoppingCartMediaPlanPickerPlanInput
        selectedMediaPlan={selectedMediaPlan}
        setSelectedMediaPlan={setSelectedMediaPlan}
        setActiveStep={setActiveStep}
      />

      <AppButton
        variant="primary"
        size="medium"
        fullWidth
        startIcon={<ShoppingCartIcon />}
        disabled={(!selectedSpaces.length && !selectedPackages.length) || !selectedMediaPlan}
        onClick={() => addToMediaPlan()}
      >
        {t('Add to Media Plan')}
      </AppButton>
    </Stack>
  );
});

export default ShoppingCartMediaPlanPicker;
